import { z } from 'zod';
import { LOCALES } from './locale.js';
import { SPHERE_IDS } from './spheres.js';
import { isDayKey, isValidTimeZone } from './calendar.js';
export const SCHEMA_VERSION = 2;
/** предел длины имени при создании навыка в UI/API */
export const SKILL_NAME_MAX_NEW = 200;
/** предел длины имени в хранимом состоянии: старые длинные имена не обрезаются */
export const SKILL_NAME_MAX_STORED = 1000;
export const zSphereId = z.enum(SPHERE_IDS);
export const zDayKey = z.string().refine(isDayKey, {
    message: 'ожидается календарная дата YYYY-MM-DD',
});
export const zTimeZone = z
    .string()
    .refine(isValidTimeZone, { message: 'ожидается известная IANA timezone' });
export const zSphereValue = z
    .number()
    .int('оценка сферы — целое число')
    .min(0)
    .max(10);
export const zSphereValues = z.object(Object.fromEntries(SPHERE_IDS.map((id) => [id, zSphereValue])));
export const zHours = z
    .number()
    .finite('часы — конечное число')
    .min(0)
    .max(24);
/** ключи, через которые в JSON приходит загрязнение прототипа */
export const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];
const zDayRecord = (value) => z.record(z.string(), value).superRefine((rec, ctx) => {
    for (const k of Object.keys(rec)) {
        if (!isDayKey(k)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [k], message: `ключ «${k}» не является датой YYYY-MM-DD` });
        }
        if (FORBIDDEN_KEYS.includes(k)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [k], message: 'запрещённый ключ' });
        }
    }
});
export const zSkillId = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'id навыка: латиница, цифры, «-» и «_»');
export const zSkill = z.object({
    id: zSkillId,
    sphere: zSphereId,
    name: z.string().min(1).max(SKILL_NAME_MAX_STORED),
    type: z.enum(['check', 'hours']),
    done: zDayRecord(z.literal(true)).default({}),
    hours: zDayRecord(zHours).default({}),
    /** со второго месяца: сколько раз в месяц человек хочет делать навык */
    goal: z.number().int().min(1).max(31).optional(),
});
export const zHistoryEntry = z.object({
    date: zDayKey,
    values: zSphereValues,
});
/** куда ведёт наблюдение: механика, главы книги и практика озера */
export const zObservationRefs = z.object({
    mech: z.string().max(40).optional(),
    chapters: z
        .array(z.object({
        chapterId: z.string().regex(/^[a-z0-9-]{1,120}$/),
        hint: z.string().min(1).max(80),
        /** фраза в главе, к которой прокрутить: описание персоны, а не начало главы */
        anchor: z.string().max(120).optional(),
        /** то же место по реестру BOOK_ANCHORS: находится в книге на любом языке */
        anchorId: z.string().regex(/^[a-z0-9-]{1,60}$/).optional(),
    }))
        .max(4)
        .default([]),
    lake: zSphereId.optional(),
    /** персона дня: «сегодня я наблюдаю тебя как …» со ссылкой на ее описание */
    persona: z
        .object({
        name: z.string().min(1).max(40),
        chapterId: z.string().regex(/^[a-z0-9-]{1,120}$/),
        anchor: z.string().max(120).optional(),
        anchorId: z.string().regex(/^[a-z0-9-]{1,60}$/).optional(),
    })
        .optional(),
});
export const zObservation = z.object({
    id: z.string().min(1).max(64),
    date: zDayKey,
    lead: z.string().max(2000),
    body: z.string().max(4000),
    act: z.string().max(2000),
    // старые записи без ссылок остаются валидными: главу для них находит methodFor
    refs: zObservationRefs.optional(),
    /** язык записи; нет поля — русский. переключение языка историю не переписывает */
    locale: z.enum(LOCALES).optional(),
});
/** свое название сферы: ключ — id сферы, он не меняется, поэтому отметки и связи с книгой целы */
export const SPHERE_NAME_MAX = 30;
export const zSphereNames = z
    .record(z.string(), z.string().trim().min(1).max(SPHERE_NAME_MAX))
    .refine((names) => Object.keys(names).every((k) => SPHERE_IDS.includes(k)), {
    message: 'неизвестная сфера',
});
export const zStateV2 = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    timezone: zTimeZone,
    values: zSphereValues,
    skills: z.array(zSkill).max(500),
    history: z.array(zHistoryEntry).max(20000),
    observations: z.array(zObservation).max(5000),
    // необязательное поле: старые состояния и старые клиенты остаются валидными
    sphereNames: zSphereNames.optional(),
});
export function emptyState(timezone) {
    return {
        schemaVersion: SCHEMA_VERSION,
        timezone: isValidTimeZone(timezone) ? timezone : 'UTC',
        values: Object.fromEntries(SPHERE_IDS.map((id) => [id, 5])),
        skills: [],
        history: [],
        observations: [],
    };
}
/** глубокая копия без прототипных ключей — вход никогда не мутируется */
export function cloneState(state) {
    return JSON.parse(JSON.stringify(state));
}
/** детерминированная сериализация: одинаковое состояние → одинаковая строка → одинаковый хэш */
export function canonicalJson(value) {
    const walk = (v) => {
        if (Array.isArray(v))
            return v.map(walk);
        if (v && typeof v === 'object') {
            const src = v;
            const out = {};
            for (const k of Object.keys(src).sort()) {
                if (FORBIDDEN_KEYS.includes(k))
                    continue;
                out[k] = walk(src[k]);
            }
            return out;
        }
        return v;
    };
    return JSON.stringify(walk(value));
}
export function formatZodError(err) {
    return err.issues.map((i) => ({
        path: i.path.length ? i.path.join('.') : '(root)',
        message: i.message,
    }));
}
//# sourceMappingURL=model.js.map